import React, {ReactNode, useCallback, useState, useEffect, useId, useRef, useSyncExternalStore} from "react"
import {
	Box,
	Button,
	TextField,
	Typography,
	CircularProgress,
	Alert,
	Divider,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Chip,
	Menu,
	MenuItem,
	Select,
	Skeleton,
	Fade,
} from "@mui/material"
import {alpha} from "@mui/material/styles"
import AttachFileIcon from "@mui/icons-material/AttachFile"
import CloseIcon from "@mui/icons-material/Close"
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome"
import SendIcon from "@mui/icons-material/Send"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"

import {useDraftReplySessions} from "./DraftReplySessions"
import type {DraftReplySession, PickableDocument} from "./DraftReplySessions"

import {useDraftReply} from "@indebted/api/conversationDraftReplyAPI"
import {useSendReply} from "@indebted/api/conversationSendReplyAPI"
import {api} from "@indebted/api"
import type {DraftReplyBlocked, DraftReplyDraft, EmailRecipient} from "@indebted/api"
import {useAgent} from "@indebted/hooks/Agent"

type DraftReplyPanelProps = {
	customerID: string
	conversationID: string
	providerMessageId: string
	customerEmails?: string[]
	repliesToEarlierMessage?: boolean
	autoGenerate?: boolean
	onSent?: () => void
}

type DocumentsState = {kind: "loading"} | {kind: "ready"; documents: PickableDocument[]} | {kind: "failed"}

const GENERIC_SEND_FAILURE = "Couldn't send the reply. Try again."

// Screen-reader-only styling for the live region that announces draft arrival
// and the send outcome.
const srOnly = {
	position: "absolute",
	width: "1px",
	height: "1px",
	margin: "-1px",
	padding: 0,
	overflow: "hidden",
	clip: "rect(0 0 0 0)",
	whiteSpace: "nowrap",
	border: 0,
} as const

// Every fact is offered on every draft; the labels caption the ones the
// backend drops for lack of data.
const FACT_FIELDS = [
	{label: "Name", field: "customer_name"},
	{label: "Balance", field: "outstanding_balance"},
	{label: "Last payment amount", field: "last_payment_amount"},
	{label: "Last payment date", field: "last_payment_date"},
	{label: "Original creditor", field: "original_creditor"},
	{label: "Payment link", field: "payment_portal_link"},
]

function DraftReplyPanel({
	customerID,
	conversationID,
	providerMessageId,
	customerEmails = [],
	repliesToEarlierMessage,
	autoGenerate,
	onSent,
}: DraftReplyPanelProps) {
	const {mutateAsync} = useDraftReply()
	const {mutateAsync: sendReply} = useSendReply()
	const [agentEmail, agentName] = useAgent()

	// The reply lives in the session, not in this mount: a remount for the same
	// message is the agent coming back, and it shows what they left. A draft or a
	// send that lands after the panel is gone still reaches the session.
	const sessions = useDraftReplySessions()
	const sessionKey = `${customerID}/${conversationID}/${providerMessageId}`
	const subscribe = useCallback(
		(onChange: () => void) => sessions.subscribe(sessionKey, onChange),
		[sessions, sessionKey],
	)
	const session = useSyncExternalStore(subscribe, () => sessions.read(sessionKey))
	const {guidance, result, body, pickedRecipient, selectedDocument, generationFailed, sendError} = session
	const drafting = session.pending === "drafting"
	const sending = session.pending === "sending"
	function update(patch: Partial<DraftReplySession>) {
		sessions.write(sessionKey, {...sessions.read(sessionKey), ...patch})
	}

	const [confirmingDiscard, setConfirmingDiscard] = useState(false)
	const [documentsState, setDocumentsState] = useState<DocumentsState>({kind: "loading"})

	const mounted = useRef(true)
	useEffect(() => {
		return () => {
			mounted.current = false
		}
	}, [])

	const loadDocuments = useCallback(() => {
		setDocumentsState({kind: "loading"})
		api.customer
			.getNotes(customerID)
			.then((response) => {
				if (!mounted.current) return
				setDocumentsState({kind: "ready", documents: noteUploads(response.Notes)})
			})
			.catch(() => {
				if (mounted.current) setDocumentsState({kind: "failed"})
			})
	}, [customerID])

	useEffect(() => {
		loadDocuments()
	}, [loadDocuments])

	// Opening a held case's conversation is the intent to reply, so the panel
	// drafts once on mount; outside a held case generation stays behind the button.
	// Coming back to a draft already made, still being made, or failed is not an
	// opening; the agent sees what happened and decides.
	useEffect(() => {
		if (autoGenerate && !result && !drafting && !generationFailed) void generateDraft()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	async function generateDraft() {
		update({pending: "drafting", generationFailed: false, sendError: null})
		try {
			const response = await mutateAsync({
				customerID,
				conversationID,
				providerMessageId,
				guidance,
			})
			if (response.status === "ok") {
				update({
					result: {
						kind: "ok",
						draft: response.draft,
						omittedFields: response.omittedFields ?? [],
						contactWarning: response.contactWarning,
						complianceFallback: response.complianceFallback ?? false,
					},
					body: response.draft.bodyText.trim(),
					pending: null,
				})
			} else {
				update({result: {kind: "blocked", reason: response.reason}, pending: null})
			}
		} catch {
			update({pending: null, generationFailed: true})
		}
	}

	function handleGenerateDraft() {
		if (result?.kind === "ok" && hasBodyChanged(body, result.draft.bodyText)) {
			setConfirmingDiscard(true)
			return
		}
		void generateDraft()
	}

	function discardEditsAndGenerate() {
		setConfirmingDiscard(false)
		void generateDraft()
	}

	async function handleApproveAndSend() {
		if (result?.kind !== "ok") return
		// The store renders synchronously, so the button is disabled before a
		// second click can land, here or on a return mid-send.
		update({pending: "sending", sendError: null})
		const edited = hasBodyChanged(body, result.draft.bodyText)
		// Unedited posts the draft's own text, trailing separator and all, so what
		// the customer receives stays byte-identical to today.
		const sentBodyText = edited ? body : result.draft.bodyText
		try {
			const response = await sendReply({
				customerID,
				conversationID,
				providerMessageId,
				bodyHtml: result.draft.bodyHtml,
				bodyText: sentBodyText,
				edited,
				guidance,
				authoredBy: agentEmail ?? "",
				modelVersion: result.draft.modelVersion,
				...(pickedRecipient ? {recipient: pickedRecipient} : {}),
				...(selectedDocument
					? {attachment: {noteId: selectedDocument.noteId, uploadId: selectedDocument.uploadId}}
					: {}),
			})
			// A 2xx that is not an acceptance comes from a handler older than this
			// build; reporting it as sent would claim a reply nothing recorded.
			if (response.status !== "accepted") {
				update({pending: null, sendError: GENERIC_SEND_FAILURE})
				return
			}
			update({sent: true, pending: null})
			if (!mounted.current) return
			onSent?.()
		} catch (error) {
			update({pending: null, sendError: rejectionMessage(error)})
		}
	}

	const sent = result?.kind === "ok" && session.sent

	// The draft writes itself, so its arrival and the send outcome are announced
	// rather than discovered; focus follows the send to the confirmation.
	const sentAlertRef = useRef<HTMLDivElement>(null)
	useEffect(() => {
		if (sent) sentAlertRef.current?.focus()
	}, [sent])

	return (
		<Box
			display="flex"
			flexDirection="column"
			gap={2}
			p={2.5}
			border="1px solid"
			borderColor="grey.200"
			borderRadius={2}
			sx={(theme) => ({backgroundColor: alpha(theme.palette.primary.main, 0.03)})}
		>
			<Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
				<Box display="flex" alignItems="center" gap={1}>
					<AutoAwesomeIcon color="primary" sx={{fontSize: 20}} />
					<Typography variant="subtitle1" component="h3" fontWeight={600} color="textPrimary">
						Draft a reply
					</Typography>
				</Box>
				{result?.kind === "ok" && !sent && (
					<Box display="flex" alignItems="center" gap={1.5}>
						<Typography
							variant="caption"
							color={
								agentEmail && offeringRecipientPicker(customerEmails, result.draft) && !pickedRecipient
									? "warning.main"
									: "textSecondary"
							}
							sx={{overflowWrap: "anywhere"}}
						>
							{agentEmail
								? `Sends to ${pickedRecipient || result.draft.to.label || result.draft.to.email}`
								: "Sign in to send replies."}
						</Typography>
						<Button
							variant="contained"
							color="primary"
							aria-label="Approve & send"
							disabled={!agentEmail || sending || drafting || isBlank(body)}
							onClick={handleApproveAndSend}
							startIcon={sending ? <CircularProgress size={16} /> : undefined}
							endIcon={sending ? undefined : <SendIcon />}
						>
							Approve & send
						</Button>
					</Box>
				)}
			</Box>
			<Box aria-live="polite" sx={srOnly}>
				{result?.kind === "ok" && !sent && !drafting
					? result.complianceFallback
						? "The assistant could not draft this reply; write it below"
						: "Draft ready to review"
					: ""}
			</Box>
			{sendError && (
				<Alert severity="error" sx={{borderRadius: 2}}>
					{sendError}
				</Alert>
			)}
			{repliesToEarlierMessage && (
				<Alert severity="warning" sx={{borderRadius: 2}}>
					Replying to an earlier message. The customer has sent newer messages since.
				</Alert>
			)}
			{generationFailed && (
				<Alert
					severity="error"
					sx={{borderRadius: 2}}
					action={
						<Button size="small" disabled={drafting || sending} onClick={handleGenerateDraft}>
							Retry
						</Button>
					}
				>
					Failed to generate the draft reply.
				</Alert>
			)}
			{drafting && !result && <DraftingPlaceholder />}
			{result?.kind === "ok" &&
				(sent ? (
					<Box
						ref={sentAlertRef}
						tabIndex={-1}
						role="alert"
						display="flex"
						gap={1.5}
						p={2.5}
						bgcolor="background.paper"
						border="1px solid"
						borderColor="grey.200"
						borderRadius={1}
					>
						<CheckCircleIcon color="success" />
						<Box display="flex" flexDirection="column" gap={0.5} minWidth={0}>
							<Typography variant="subtitle2" fontWeight={600} color="textPrimary">
								Reply sent
							</Typography>
							<Typography variant="body2" color="textSecondary" sx={{overflowWrap: "anywhere"}}>
								To {pickedRecipient || recipientDisplay(result.draft.to)} · by {agentName ?? agentEmail}
							</Typography>
							{selectedDocument && (
								<Chip
									label={selectedDocument.fileName}
									size="small"
									icon={<AttachFileIcon />}
									sx={{alignSelf: "flex-start"}}
								/>
							)}
						</Box>
					</Box>
				) : (
					<Fade in appear timeout={500}>
						<Box>
							<DraftPreview
								draft={result.draft}
								body={body}
								onBodyChange={(next) => update({body: next})}
								omittedFields={result.omittedFields}
								contactWarning={result.contactWarning}
								complianceFallback={result.complianceFallback}
								isSending={sending}
								isRegenerating={drafting}
								customerEmails={customerEmails}
								pickedRecipient={pickedRecipient}
								onPickRecipient={(next) => update({pickedRecipient: next})}
								attachmentArea={
									<AttachmentArea
										state={documentsState}
										selected={selectedDocument}
										disabled={drafting || sending}
										onSelect={(document) => update({selectedDocument: document})}
										onRemove={() => update({selectedDocument: null})}
										onRetry={loadDocuments}
									/>
								}
							/>
						</Box>
					</Fade>
				))}
			{result?.kind === "blocked" && <BlockedBanner reason={result.reason} />}
			{!sent && (
				<>
					<Divider />
					<Box display="flex" flexDirection="column" gap={1.5}>
						<Typography variant="subtitle2" component="h4" fontWeight={600} color="textPrimary">
							Adjust the draft
						</Typography>
						<TextField
							label="Instructions"
							placeholder="Tell the assistant how to reply"
							value={guidance}
							onChange={(event) => update({guidance: event.target.value})}
							multiline
							minRows={2}
							fullWidth
							size="small"
							sx={{"& .MuiOutlinedInput-root": {backgroundColor: "background.paper"}}}
						/>
						<Button
							variant="outlined"
							aria-label={result ? "Regenerate draft" : "Generate draft"}
							disabled={drafting || sending}
							onClick={handleGenerateDraft}
							startIcon={drafting ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
							sx={{alignSelf: "flex-start", backgroundColor: "background.paper"}}
						>
							{result ? "Regenerate draft" : "Generate draft"}
						</Button>
					</Box>
				</>
			)}
			<DiscardEditsDialog
				open={confirmingDiscard}
				onCancel={() => setConfirmingDiscard(false)}
				onConfirm={discardEditsAndGenerate}
			/>
		</Box>
	)
}

// Trim the same way the seeding does, or the field and this comparison disagree
// about the baseline.
function hasBodyChanged(body: string, draftBodyText: string) {
	return body.trim() !== draftBodyText.trim()
}

function isBlank(body: string) {
	return body.trim() === ""
}

// The reply addresses the inbound sender by default, so the choice is only
// offered when that address is not one of the customer's own emails — the
// web-form relay case, where the default would send the reply nowhere useful.
function offeringRecipientPicker(customerEmails: string[], draft: DraftReplyDraft) {
	return customerEmails.length > 0 && !customerEmails.includes(draft.to.email)
}

// The API client flattens a rejected send into its backend message, so a refusal
// explains itself rather than telling the agent to try something that cannot work.
function rejectionMessage(error: unknown): string {
	if (error && typeof error === "object" && "Message" in error && typeof error.Message === "string") {
		return error.Message
	}
	return GENERIC_SEND_FAILURE
}

function DiscardEditsDialog({open, onCancel, onConfirm}: {open: boolean; onCancel: () => void; onConfirm: () => void}) {
	return (
		<Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
			<DialogTitle>Discard your edits?</DialogTitle>
			<DialogContent>
				<Typography variant="body2" color="text.secondary">
					Generating another draft replaces the reply body, and the changes you have made to it will be lost.
				</Typography>
			</DialogContent>
			<DialogActions>
				<Button onClick={onCancel} size="small">
					Keep editing
				</Button>
				<Button variant="contained" size="small" onClick={onConfirm}>
					Discard &amp; generate
				</Button>
			</DialogActions>
		</Dialog>
	)
}

// The first draft writes itself on open, so the wait shows where the email
// will land, shaped like the email it becomes: sender and recipient lines,
// subject, body, and the send action.
function DraftingPlaceholder() {
	return (
		<Box role="status" aria-label="Drafting reply" display="flex" flexDirection="column" gap={1}>
			<Box display="flex" alignItems="center" gap={0.75}>
				<CircularProgress size={12} />
				<Typography variant="caption" color="textSecondary">
					Drafting a reply…
				</Typography>
			</Box>
			<Box
				border="1px solid"
				borderColor="grey.200"
				borderRadius={1}
				overflow="hidden"
				bgcolor="background.paper"
			>
				<Box px={2.5} pt={2} pb={1.5} display="flex" flexDirection="column" gap={0.5}>
					<Skeleton variant="text" width="30%" />
					<Skeleton variant="text" width="40%" />
					<Skeleton variant="text" width="55%" />
				</Box>
				<Divider />
				<Box px={2.5} py={2} display="flex" flexDirection="column" gap={0.5}>
					<Skeleton variant="text" width="90%" />
					<Skeleton variant="text" width="85%" />
					<Skeleton variant="text" width="70%" />
					<Skeleton variant="text" width="40%" />
				</Box>
			</Box>
			<Skeleton variant="rounded" width={160} height={36} />
		</Box>
	)
}

function PanelColumn({children}: {children: ReactNode}) {
	return (
		<Box display="flex" flexDirection="column" gap={2}>
			{children}
		</Box>
	)
}

// noteUploads keeps only active notes, matching the notes surface where an
// archived note's upload is not downloadable.
function noteUploads(
	notes: Array<{ID: string; Status: {ID: string}; Uploads: Array<{ID: string; FileName: string}>}>,
): PickableDocument[] {
	return notes
		.filter((note) => note.Status.ID === "active")
		.flatMap((note) =>
			(note.Uploads ?? []).map((upload) => ({noteId: note.ID, uploadId: upload.ID, fileName: upload.FileName})),
		)
}

// The attachment travels with the email, so it is picked and shown inside the
// drafted email the agent approves, never beside it.
function AttachmentArea({
	state,
	selected,
	disabled,
	onSelect,
	onRemove,
	onRetry,
}: {
	state: DocumentsState
	selected: PickableDocument | null
	disabled: boolean
	onSelect: (document: PickableDocument) => void
	onRemove: () => void
	onRetry: () => void
}) {
	const [anchor, setAnchor] = useState<HTMLElement | null>(null)

	if (state.kind === "loading") {
		return <Skeleton variant="rounded" width={150} height={28} />
	}

	if (state.kind === "failed") {
		return (
			<Alert
				severity="warning"
				sx={{borderRadius: 2}}
				action={
					<Button size="small" disabled={disabled} onClick={onRetry}>
						Retry
					</Button>
				}
			>
				{"Couldn't load this customer's documents."}
			</Alert>
		)
	}

	if (state.documents.length === 0) {
		return (
			<Typography variant="caption" color="textSecondary">
				No documents on file to attach.
			</Typography>
		)
	}

	if (selected) {
		return (
			<Chip
				label={selected.fileName}
				title={selected.fileName}
				size="small"
				disabled={disabled}
				icon={<AttachFileIcon />}
				deleteIcon={<CloseIcon titleAccess={`Remove ${selected.fileName}`} />}
				onDelete={onRemove}
				sx={{alignSelf: "flex-start"}}
			/>
		)
	}

	return (
		<>
			<Button
				variant="outlined"
				size="small"
				disabled={disabled}
				startIcon={<AttachFileIcon />}
				onClick={(event) => setAnchor(event.currentTarget)}
				sx={{alignSelf: "flex-start"}}
			>
				Attach document
			</Button>
			<Menu
				anchorEl={anchor}
				open={Boolean(anchor)}
				onClose={() => setAnchor(null)}
				slotProps={{paper: {sx: {maxWidth: 360}}}}
			>
				{state.documents.map((document) => (
					<MenuItem
						key={document.uploadId}
						title={document.fileName}
						sx={{display: "block", overflow: "hidden", textOverflow: "ellipsis"}}
						onClick={() => {
							onSelect(document)
							setAnchor(null)
						}}
					>
						{document.fileName}
					</MenuItem>
				))}
			</Menu>
		</>
	)
}

function DraftPreview({
	draft,
	body,
	onBodyChange,
	omittedFields,
	contactWarning,
	complianceFallback,
	isSending,
	isRegenerating,
	customerEmails,
	pickedRecipient,
	onPickRecipient,
	attachmentArea,
}: {
	draft: DraftReplyDraft
	body: string
	onBodyChange: (body: string) => void
	omittedFields: string[]
	contactWarning?: string
	complianceFallback: boolean
	isSending: boolean
	isRegenerating: boolean
	customerEmails: string[]
	pickedRecipient: string
	onPickRecipient: (recipient: string) => void
	attachmentArea: ReactNode
}) {
	const offerRecipientPicker = offeringRecipientPicker(customerEmails, draft)
	const omittedLabels = omittedFields
		.map((field) => FACT_FIELDS.find((fact) => fact.field === field)?.label)
		.filter((label): label is string => Boolean(label))

	return (
		<PanelColumn>
			{isRegenerating && (
				<Box role="status" display="flex" alignItems="center" gap={1}>
					<CircularProgress size={14} />
					<Typography variant="caption" color="textSecondary">
						Rewriting the draft…
					</Typography>
				</Box>
			)}
			<Box
				aria-busy={isRegenerating}
				display="flex"
				flexDirection="column"
				gap={2}
				sx={{opacity: isRegenerating ? 0.5 : 1}}
			>
				{contactWarning && (
					<Alert severity="warning" sx={{borderRadius: 2}}>
						This customer has an active {contactWarning} case.
					</Alert>
				)}
				{complianceFallback && (
					<Alert severity="warning" sx={{borderRadius: 2}}>
						{
							"The assistant couldn't write a reply that passed compliance review. Write the reply yourself below; the disclosure footer is already in place."
						}
					</Alert>
				)}
				<Box
					component="section"
					aria-label="Drafted email"
					border="1px solid"
					borderColor="grey.200"
					borderRadius={1}
					overflow="hidden"
					bgcolor="background.paper"
					sx={{"&:focus-within": {borderColor: "primary.main"}}}
				>
					<Box px={2.5} pt={2} pb={1.5} display="flex" flexDirection="column" gap={1}>
						<Box
							display="grid"
							gridTemplateColumns="max-content 1fr"
							columnGap={1.5}
							rowGap={0.25}
							alignItems="baseline"
						>
							<RecipientRow label="From" recipient={draft.from} />
							{offerRecipientPicker ? (
								<RecipientPickerRow
									conversationRecipient={draft.to}
									customerEmails={customerEmails}
									picked={pickedRecipient}
									onPick={onPickRecipient}
									disabled={isSending || isRegenerating}
								/>
							) : (
								<RecipientRow label="To" recipient={draft.to} />
							)}
						</Box>
						<Typography variant="subtitle2" color="textPrimary">
							{draft.subject}
						</Typography>
					</Box>
					<Divider />
					<TextField
						placeholder="Write the reply"
						value={body}
						onChange={(event) => onBodyChange(event.target.value)}
						disabled={isRegenerating}
						multiline
						minRows={8}
						fullWidth
						variant="standard"
						slotProps={{
							input: {
								disableUnderline: true,
								sx: {px: 2.5, py: 2, fontSize: "0.875rem", lineHeight: 1.65},
							},
							htmlInput: {"aria-label": "Reply body"},
						}}
					/>
					{draft.footerText && (
						<>
							<Divider />
							<Typography
								variant="caption"
								color="textSecondary"
								sx={{whiteSpace: "pre-wrap", display: "block", px: 2.5, py: 1.5}}
							>
								{draft.footerText}
							</Typography>
						</>
					)}
					<Divider />
					<Box px={2.5} py={1.5}>
						{attachmentArea}
					</Box>
				</Box>

				{omittedLabels.length > 0 && (
					<Alert severity="info" sx={{borderRadius: 2}}>
						Not included: no data on file for {omittedLabels.join(", ")}
					</Alert>
				)}
			</Box>
		</PanelColumn>
	)
}

function recipientDisplay(recipient: EmailRecipient): string {
	return recipient.label ? `${recipient.label} <${recipient.email}>` : recipient.email
}

function RecipientRow({label, recipient}: {label: string; recipient: EmailRecipient}) {
	return (
		<>
			<Typography variant="caption" color="textSecondary" textAlign="right">
				{label}
			</Typography>
			<Typography variant="body2" color="textPrimary" sx={{overflowWrap: "anywhere"}}>
				{recipientDisplay(recipient)}
			</Typography>
		</>
	)
}

// The conversation address stays the default so nothing is redirected silently;
// the picker offers the customer's own emails beside it and warns while the
// reply is still aimed at an address that is not the customer's.
function RecipientPickerRow({
	conversationRecipient,
	customerEmails,
	picked,
	onPick,
	disabled,
}: {
	conversationRecipient: EmailRecipient
	customerEmails: string[]
	picked: string
	onPick: (recipient: string) => void
	disabled: boolean
}) {
	const warningId = useId()

	return (
		<>
			<Typography variant="caption" color="textSecondary" textAlign="right">
				To
			</Typography>
			<Box display="flex" flexDirection="column" gap={0.25} minWidth={0}>
				<Select
					value={picked || conversationRecipient.email}
					onChange={(event) =>
						onPick(event.target.value === conversationRecipient.email ? "" : event.target.value)
					}
					disabled={disabled}
					variant="standard"
					disableUnderline
					inputProps={{"aria-label": "Reply recipient", ...(picked ? {} : {"aria-describedby": warningId})}}
					MenuProps={{slotProps: {paper: {sx: {maxWidth: 360}}}}}
					sx={{fontSize: "0.875rem", alignSelf: "flex-start", maxWidth: "100%"}}
				>
					<MenuItem value={conversationRecipient.email}>
						{recipientDisplay(conversationRecipient)} — conversation address
					</MenuItem>
					{customerEmails.map((email) => (
						<MenuItem
							key={email}
							value={email}
							title={email}
							sx={{display: "block", overflow: "hidden", textOverflow: "ellipsis"}}
						>
							{email}
						</MenuItem>
					))}
				</Select>
				{!picked && (
					<Typography id={warningId} variant="caption" color="warning.main">
						{
							"Not one of the customer's emails — the reply may not reach them. Pick one of the customer's emails to redirect it."
						}
					</Typography>
				)}
			</Box>
		</>
	)
}

function BlockedBanner({reason}: {reason: DraftReplyBlocked["reason"]}) {
	// A reason this build does not know is one the API gained or has not yet
	// dropped; naming the wrong cause is worse than naming none.
	const message =
		reason === "prompt_injection"
			? "The customer's message tried to manipulate the assistant, so it won't draft a reply here. Review the message yourself and escalate if something looks wrong."
			: reason === "conduct_exhausted"
				? "The assistant couldn't write a reply that passed compliance review. Add instructions and generate again."
				: "This reply was blocked. Try again, and escalate if it keeps happening."

	return (
		<Alert severity="warning" sx={{borderRadius: 2}}>
			{message}
		</Alert>
	)
}

export {DraftReplyPanel}
